/**
 * The skin shop.
 *
 * Every skin is locked until it is bought, and rubies come from playing: five
 * for winning an online match, one for turning up, one for a new personal best
 * at a solo game. Turning up pays because a nine year old who loses five in a
 * row should still be saving toward something.
 *
 * The previews come from `skinPreview.ts`, shared with the lobby, rather than
 * booting a renderer for twenty four thumbnails. The hub must stay light:
 * pulling the arena's renderer in here to draw a basketball would undo the
 * whole reason the hub is a separate page.
 */
import { SKINS, type SkinDef } from '@arena/games';

import { paintSkin } from '../skinPreview.js';

import { buySkin, equipSkin, fetchShop, type ShopState } from './identity.js';
import { h } from './kit.js';

/**
 * Build the shop page.
 *
 * Refetches after every purchase rather than adjusting a local balance,
 * because the balance is the server's and a client that keeps its own copy will
 * eventually disagree with it in front of a child.
 */
export function renderShop(root: HTMLElement, onChange: () => void): void {
  const wrap = h('div', 'ac-shop');
  root.append(wrap);
  void paint();

  async function paint(): Promise<void> {
    const shop = await fetchShop();
    wrap.replaceChildren();

    const head = h('div', 'ac-shop-head');
    head.append(
      h('h3', '', 'Apparences'),
      h(
        'p',
        'ac-note',
        'Gagne des rubis en jouant: 5 pour une victoire en ligne, 1 par partie, ' +
          '1 pour chaque record personnel.',
      ),
    );
    const purse = h('div', 'ac-purse');
    purse.append(h('span', 'ac-ruby', '♦'), h('strong', '', String(shop?.rubies ?? 0)));
    head.append(purse);
    wrap.append(head);

    const grid = h('div', 'ac-skins');

    // "None" is a real choice, and the one everybody starts on.
    grid.append(tile(null, true, shop?.skin === '', shop));
    for (const skin of SKINS) {
      const owned = shop?.owned.includes(skin.id) ?? false;
      grid.append(tile(skin, owned, shop?.skin === skin.id, shop));
    }
    wrap.append(grid);
  }

  function tile(
    skin: SkinDef | null,
    owned: boolean,
    worn: boolean,
    shop: ShopState | null,
  ): HTMLElement {
    const card = h('div', 'ac-skin' + (worn ? ' is-worn' : '') + (owned ? '' : ' is-locked'));
    const canvas = h('canvas', 'ac-skin-art') as HTMLCanvasElement;
    paintSkin(canvas, skin, 72);
    card.append(canvas, h('span', 'ac-skin-name', skin ? skin.name : 'Aucune'));

    if (worn) {
      card.append(h('span', 'ac-skin-tag', 'portée'));
      return card;
    }

    if (owned) {
      const use = h('button', 'ac-ghost ac-skin-btn', 'Porter');
      use.addEventListener('click', async () => {
        use.textContent = '...';
        await equipSkin(skin ? skin.id : '');
        await paint();
        onChange();
      });
      card.append(use);
      return card;
    }

    const afford = (shop?.rubies ?? 0) >= (skin?.cost ?? 0);
    const buy = h('button', 'ac-skin-btn ' + (afford ? 'ac-primary' : 'ac-ghost'));
    buy.append(h('span', 'ac-ruby', '♦'), document.createTextNode(` ${skin?.cost ?? 0}`));
    buy.addEventListener('click', async () => {
      if (!skin) return;
      buy.textContent = '...';
      const result = await buySkin(skin.id);
      if (result && !result.ok) {
        // Say which refusal it was. "Nothing happened" is the worst answer.
        buy.textContent = result.reason === 'poor' ? 'Pas assez' : 'Impossible';
        window.setTimeout(() => void paint(), 1200);
        return;
      }
      // Bought: wear it straight away, which is what anybody wants next.
      if (skin) await equipSkin(skin.id);
      await paint();
      onChange();
    });
    card.append(buy);
    return card;
  }
}
